import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Car, Pencil, Trash2, Phone, Mail, UserPlus, Search, LayoutGrid, List as ListIcon, History } from "lucide-react";
import { toast } from "sonner";
import VehicleDialog from "@/components/VehicleDialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import SubscriptionBadge, { clientSubscriptionSummary } from "@/components/SubscriptionBadge";
import FuelBadge from "@/components/FuelBadge";
import { useSettings } from "@/context/SettingsContext";
import { missionLabel, money, fmtDateTime } from "@/lib/helpers";
import { useVocab } from "@/lib/vocab";
import { formatPhone } from "@/lib/phone";
import { ORDERER_KINDS } from "@/lib/ride";

export default function Clients() {
  // Transport (lot T1a) : les véhicules sont ceux de la flotte (page Véhicules), pas ceux des clients.
  const transport = useVocab().business === "transport";
  // Téléphone international enregistré (« +33611223344 ») affiché lisiblement en transport (lot T6) ; M CARWASH : tel quel.
  const tel = (v) => (transport ? formatPhone(v) : v);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [missions, setMissions] = useState([]);
  const [subs, setSubs] = useState([]);
  const [fuel, setFuel] = useState({}); // comptes carburant par client
  const [cDialog, setCDialog] = useState({ open: false, item: null });
  const [vDialog, setVDialog] = useState({ open: false, item: null, clientId: null });
  const [hDialog, setHDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null); // { kind: "client" | "vehicle", id }
  const [query, setQuery] = useState("");
  const [view, setView] = useState("cards");

  const load = useCallback(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
    api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/fuel-accounts").then((r) => setFuel(Object.fromEntries(r.data.map((a) => [a.client_id, a])))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Suppression après confirmation ; un client ou véhicule avec historique est refusé par le serveur (message affiché).
  const confirmDelete = async () => {
    const { kind, id } = toDelete; setToDelete(null);
    try {
      await api.delete(kind === "client" ? `/clients/${id}` : `/vehicles/${id}`);
      toast.success(kind === "client" ? "Client supprimé" : "Véhicule supprimé"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delClient = (id) => setToDelete({ kind: "client", id });
  const delVehicle = (id) => setToDelete({ kind: "vehicle", id });
  const convertClient = async (id) => { await api.post(`/clients/${id}/convert`); toast.success("Prospect converti en client"); load(); };
  const filtered = clients.filter((c) => { const q = query.trim().toLowerCase(); if (!q) return true; return (c.name || "").toLowerCase().includes(q) || (c.company || "").toLowerCase().includes(q) || (c.phone || "").includes(q); });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Répertoire</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">{transport ? "Clients" : "Clients & véhicules"}</h1>
        </div>
        <Button className="gap-2" onClick={() => setCDialog({ open: true, item: null })} data-testid="new-client-button"><Plus className="h-4 w-4" /> Client</Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un client par nom, société ou téléphone…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="client-search" />
        </div>
        <div className="flex border border-border rounded-lg overflow-hidden shrink-0">
          <button className={`px-3 py-2 ${view === "cards" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`} onClick={() => setView("cards")} data-testid="view-cards"><LayoutGrid className="h-4 w-4" /></button>
          <button className={`px-3 py-2 ${view === "list" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`} onClick={() => setView("list")} data-testid="view-list"><ListIcon className="h-4 w-4" /></button>
        </div>
      </div>
      {view === "list" ? (
        <div className="border border-border rounded-2xl divide-y divide-border overflow-hidden">
          {filtered.length === 0 && <p className="p-4 text-sm text-muted-foreground">{query ? "Aucun client ne correspond." : "Aucun client."}</p>}
          {filtered.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-4 py-3 gap-3" data-testid={`client-row-${c.id}`}>
              <div className="min-w-0">
                {/* flex-wrap : quand la place manque, les pastilles (abonnement, carburant) passent sous le nom au lieu d'être coupées. */}
                <div className="font-medium flex flex-wrap items-center gap-2">{c.name}{c.status === "prospect" && <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase rounded-full bg-amber-100 text-amber-700 border border-amber-200">Prospect</span>}<SubscriptionBadge summary={clientSubscriptionSummary(subs, c.id)} testid={`client-sub-row-${c.id}`} /><FuelBadge account={fuel[c.id]} testid={`client-fuel-row-${c.id}`} /></div>
                <div className="text-xs text-muted-foreground truncate">{[c.company, tel(c.phone), c.email].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                {c.status === "prospect" && <Button size="icon" variant="ghost" onClick={() => convertClient(c.id)} title="Convertir"><UserPlus className="h-4 w-4 text-emerald-600" /></Button>}
                <Button size="icon" variant="ghost" onClick={() => setHDialog({ open: true, item: c })} title="Historique des missions" data-testid={`client-history-${c.id}`}><History className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setCDialog({ open: true, item: c })}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => delClient(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="grid lg:grid-cols-2 gap-4">
        {/* 2 cartes côte à côte à partir de 1024 px ; en dessous, une par ligne (à 768 px, la ligne d'un véhicule sortait de la carte). */}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground">{query ? "Aucun client ne correspond." : "Aucun client."}</p>}
        {filtered.map((c) => (
          // min-w-0 : la carte peut rétrécir dans la grille (un e-mail long ne l'élargit plus au-delà de l'écran).
          <div key={c.id} className="bg-card border border-border rounded-2xl shadow-card p-5 min-w-0" data-testid={`client-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button className="font-display font-bold text-lg hover:text-accent hover:underline text-left" onClick={() => setHDialog({ open: true, item: c })} title="Voir l'historique des missions" data-testid={`client-name-${c.id}`}>{c.name}</button>
                  {c.status === "prospect" && <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid={`prospect-pill-${c.id}`}>Nouveau · Prospect</span>}
                  <SubscriptionBadge summary={clientSubscriptionSummary(subs, c.id)} testid={`client-sub-${c.id}`} />
                  <FuelBadge account={fuel[c.id]} testid={`client-fuel-${c.id}`} />
                </div>
                {c.company && <div className="text-sm text-muted-foreground">{c.company}</div>}
                <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                  {c.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{tel(c.phone)}</span>}
                  {/* Un e-mail trop long pour la ligne se coupe où il faut (break-words) au lieu de sortir de la carte. */}
                  {c.email && <span className="flex items-center gap-1 min-w-0"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 break-words">{c.email}</span></span>}
                </div>
                {c.phone && <a href={`https://wa.me/${c.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 mt-1 inline-block" data-testid={`wa-${c.id}`}>Ouvrir WhatsApp →</a>}
              </div>
              <div className="flex gap-1 shrink-0">
                {c.status === "prospect" && <Button size="icon" variant="ghost" onClick={() => convertClient(c.id)} title="Convertir en client" data-testid={`client-convert-${c.id}`}><UserPlus className="h-4 w-4 text-emerald-600" /></Button>}
                <Button size="icon" variant="ghost" onClick={() => setHDialog({ open: true, item: c })} title="Historique des missions" data-testid={`client-history-card-${c.id}`}><History className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setCDialog({ open: true, item: c })} data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => delClient(c.id)} data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            {c.notes && <p className="text-sm mt-3 text-muted-foreground border-t border-border pt-3">{c.notes}</p>}
            {(!transport || vehicles.some((v) => v.client_id === c.id)) && <div className="mt-3 space-y-1.5">
              {vehicles.filter((v) => v.client_id === c.id).map((v) => (
                <div key={v.id} className="flex items-center justify-between text-sm bg-muted/40 px-3 py-2" data-testid={`vehicle-${v.id}`}>
                  <span className="min-w-0">
                    {/* Marque, plaque et couleur dans un seul bloc de texte : sur une ligne trop courte, il se coupe comme une
                        phrase (plaque toujours entière). l'écart de 8 px d'avant est une marge (mr-2) placée AVANT
                        la coupure possible (<wbr />) : si la ligne se coupe là, elle reste en bout de ligne et la suite repart au bord ;
                        les espaces insécables gardent chaque « · » avec son voisin (« Badge · », « · Noir Diamant »). */}
                    <span className="flex items-center gap-2"><Car className="h-4 w-4 text-accent shrink-0" /><span className="min-w-0">{v.brand} {v.model}{v.plate && <>{" ·"}<span className="mr-2" /><wbr /><span className="font-mono whitespace-nowrap">{v.plate}</span></>}{v.color && <><span className="mr-2" /><wbr /><span className="text-muted-foreground">{"· "}{v.color}</span></>}</span></span>
                    {v.notes && <span className="block text-xs text-muted-foreground pl-6">{v.notes}</span>}
                  </span>
                  {/* Zones de clic agrandies (24 × 30 px) sans changer l'apparence : marge intérieure compensée par une
                      marge négative ; chaque bouton s'étend vers l'extérieur et ne prend que la moitié de l'espace entre eux. */}
                  <span className="flex gap-1 shrink-0">
                    <button className="pl-2 pr-0.5 py-2 -ml-2 -mr-0.5 -my-2" onClick={() => setVDialog({ open: true, item: v, clientId: c.id })} title="Modifier le véhicule" data-testid={`vehicle-edit-${v.id}`}><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
                    <button className="pl-0.5 pr-2 py-2 -ml-0.5 -mr-2 -my-2" onClick={() => delVehicle(v.id)} title="Supprimer le véhicule" data-testid={`vehicle-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></button>
                  </span>
                </div>
              ))}
              {!transport && <Button size="sm" variant="outline" className="gap-1 w-full" onClick={() => setVDialog({ open: true, item: null, clientId: c.id })} data-testid={`add-vehicle-${c.id}`}><Plus className="h-3.5 w-3.5" /> Véhicule</Button>}
            </div>}
          </div>
        ))}
      </div>
      )}

      <ClientDialog state={cDialog} setState={setCDialog} onSaved={load} />
      <VehicleDialog state={vDialog} setState={setVDialog} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{toDelete?.kind === "client" ? "Supprimer ce client ?" : "Supprimer ce véhicule ?"}</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-no">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-yes">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ClientHistorySheet state={hDialog} setState={setHDialog} missions={missions} vehicles={vehicles} />
    </div>
  );
}

function ClientHistorySheet({ state, setState, missions, vehicles }) {
  const { settings } = useSettings();
  const voc = useVocab();
  const cur = settings?.currency || "EUR";
  const client = state.item;
  const now = new Date();
  const forClient = (missions || [])
    .filter((m) => client && m.client_id === client.id)
    .sort((a, b) => new Date(b.start) - new Date(a.start));
  const upcoming = forClient.filter((m) => new Date(m.start) >= now).reverse();
  const past = forClient.filter((m) => new Date(m.start) < now);
  const totalAmount = forClient.filter((m) => m.status !== "cancelled").reduce((sum, m) => sum + (m.price_ttc || 0), 0);

  const vehicleLabel = (id) => {
    const v = vehicles.find((x) => x.id === id);
    return v ? `${v.brand} ${v.model}${v.plate ? ` · ${v.plate}` : ""}` : null;
  };

  const Row = ({ m }) => {
    const vLabel = vehicleLabel(m.vehicle_id);
    return (
      <div className="border border-border rounded-xl p-3 space-y-1" data-testid={`client-history-mission-${m.id}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">{missionLabel(m)}</span>
          <StatusBadge status={m.status} />
        </div>
        <div className="text-sm text-muted-foreground">{fmtDateTime(m.start)} → {fmtDateTime(m.end)}</div>
        {vLabel ? (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">{vLabel}</span>
            <span className="font-medium">{money(m.price_ttc, cur)}</span>
          </div>
        ) : (
          <div className="text-sm text-right font-medium">{money(m.price_ttc, cur)}</div>
        )}
      </div>
    );
  };

  return (
    <Sheet open={state.open} onOpenChange={(o) => setState({ ...state, open: o })}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="client-history-sheet">
        <SheetHeader>
          <SheetTitle className="font-display">{client?.name}</SheetTitle>
          <SheetDescription>{[client?.company, client?.phone, client?.email].filter(Boolean).join(" · ") || `Historique des ${voc.missions}`}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex items-center gap-4 bg-muted/50 border border-border rounded-xl px-4 py-3" data-testid="client-history-summary">
          <div>
            <div className="font-display font-black text-2xl leading-none">{forClient.length}</div>
            <div className="text-[10px] uppercase tracking-overline text-muted-foreground mt-1">{forClient.length > 1 ? voc.Missions : voc.Mission}</div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div>
            <div className="font-display font-black text-2xl leading-none text-accent">{money(totalAmount, cur)}</div>
            <div className="text-[10px] uppercase tracking-overline text-muted-foreground mt-1">Cumulé</div>
          </div>
        </div>
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="text-xs uppercase tracking-overline text-accent mb-2">À venir ({upcoming.length})</h3>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">{`Aucune ${voc.mission} à venir.`}</p>
            ) : (
              <div className="space-y-2">{upcoming.map((m) => <Row key={m.id} m={m} />)}</div>
            )}
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-overline text-muted-foreground mb-2">Passées ({past.length})</h3>
            {past.length === 0 ? (
              <p className="text-sm text-muted-foreground">{`Aucune ${voc.mission} passée.`}</p>
            ) : (
              <div className="space-y-2">{past.map((m) => <Row key={m.id} m={m} />)}</div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ClientDialog({ state, setState, onSaved }) {
  const { settings } = useSettings();
  const voc = useVocab();
  const transport = voc.business === "transport"; // type de donneur d'ordre : transport seulement (lot T1a)
  const cur = settings?.currency || "EUR";
  const empty = { name: "", company: "", phone: "", email: "", address: "", notes: "", tarifs: [] };
  const [form, setForm] = useState(empty);
  useEffect(() => { setForm(state.item ? { ...empty, ...state.item, tarifs: state.item.tarifs || [] } : empty); }, [state.open, state.item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTarif = (i, k, v) => setForm((f) => ({ ...f, tarifs: f.tarifs.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const addTarif = () => setForm((f) => ({ ...f, tarifs: [...f.tarifs, { prestation: "", price_ttc: "" }] }));
  const delTarif = (i) => setForm((f) => ({ ...f, tarifs: f.tarifs.filter((_, j) => j !== i) }));
  const prestations = settings?.mission_types || [];
  const save = async () => {
    if (!form.name) { toast.error("Le nom est requis"); return; }
    if (form.tarifs.some((t) => t.prestation && (t.price_ttc === "" || isNaN(parseFloat(t.price_ttc))))) { toast.error("Un tarif n'a pas de prix"); return; }
    // les lignes sans prestation choisie sont ignorées ; "tarifs" est toujours envoyé (jamais effacé par oubli)
    const tarifs = form.tarifs.filter((t) => t.prestation).map((t) => ({ prestation: t.prestation, price_ttc: parseFloat(t.price_ttc) }));
    const payload = { ...form, tarifs };
    try {
      if (state.item) await api.patch(`/clients/${state.item.id}`, payload); else await api.post("/clients", payload);
      toast.success("Enregistré"); onSaved(); setState({ open: false, item: null });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={state.open} onOpenChange={(o) => setState({ ...state, open: o })}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{state.item ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {[["name", "Nom"], ["company", "Société"], ["phone", "Téléphone / WhatsApp"], ["email", "Email"], ["address", "Adresse"]].map(([k, l]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`client-${k}`} /></div>
          ))}
          {transport && <div className="space-y-1.5"><Label>Type de donneur d'ordre</Label>
            <Select value={form.orderer_kind || "none"} onValueChange={(v) => set("orderer_kind", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="client-orderer-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non précisé</SelectItem>
                {Object.entries(ORDERER_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select></div>}
          <div className="space-y-1.5"><Label>Notes internes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="client-notes" /></div>
          {/* Transport (lot T2) : grille cachée (le dernier prix pratiqué la remplace) ; ses données sont gardées et renvoyées
              telles quelles. Chez M CARWASH : inchangée. */}
          {!transport && <div className="space-y-2" data-testid="client-tarifs">
            <Label>Grille tarifaire (prix TTC négociés)</Label>
            {form.tarifs.length === 0 && <p className="text-xs text-muted-foreground">{`Aucun tarif : prix saisi à la main sur chaque ${voc.mission}.`}</p>}
            {form.tarifs.map((t, i) => {
              const prises = form.tarifs.filter((_, j) => j !== i).map((x) => x.prestation);
              const options = prestations.filter((p) => !prises.includes(p));
              if (t.prestation && !options.includes(t.prestation)) options.push(t.prestation); // prestation renommée dans Réglages : on la garde visible
              return (
                <div key={i} className="flex items-center gap-2" data-testid={`tarif-row-${i}`}>
                  <Select value={t.prestation || undefined} onValueChange={(v) => setTarif(i, "prestation", v)}>
                    <SelectTrigger className="flex-1 min-w-0" data-testid={`tarif-prestation-${i}`}><SelectValue placeholder="Prestation" /></SelectTrigger>
                    <SelectContent>{options.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="0" step="0.01" className="w-24 shrink-0" placeholder={cur} value={t.price_ttc} onChange={(e) => setTarif(i, "price_ttc", e.target.value)} data-testid={`tarif-price-${i}`} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => delTarif(i)} data-testid={`tarif-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              );
            })}
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addTarif} disabled={form.tarifs.length >= prestations.length} data-testid="tarif-add"><Plus className="h-4 w-4" /> Ajouter un tarif</Button>
          </div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setState({ open: false, item: null })}>Annuler</Button><Button onClick={save} data-testid="client-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
